/**
 * Constrained BIOES Viterbi decoder. Direct port of `ViterbiDecoder.kt`.
 *
 * Plain argmax frequently emits invalid sequences like `B-email O E-email`
 * that fragment a single span into noise. This decoder enforces the BIOES
 * transition graph: a `B-` or `I-` is followed only by an `I-` or `E-` of
 * the same class; an `O`, `E-`, or `S-` is followed only by an opener
 * (`O`, `B-`, `S-`). Invalid transitions cost -∞ so the best legal
 * sequence is what gets returned.
 */

const NEG_INF = -1e30;

interface Tag {
  prefix: "O" | "B" | "I" | "E" | "S" | "?";
  cls: string;
}

function splitTag(label: string): Tag {
  if (label === "O") return { prefix: "O", cls: "" };
  const dash = label.indexOf("-");
  if (dash >= 0) {
    const p = label.substring(0, dash);
    return {
      prefix: (p === "B" || p === "I" || p === "E" || p === "S" ? p : "?"),
      cls: label.substring(dash + 1),
    };
  }
  return { prefix: "?", cls: "" };
}

function allowed(from: Tag, to: Tag): boolean {
  switch (from.prefix) {
    case "O":
    case "E":
    case "S":
      return to.prefix === "O" || to.prefix === "B" || to.prefix === "S";
    case "B":
    case "I":
      return (to.prefix === "I" || to.prefix === "E") && to.cls === from.cls;
    default:
      return false;
  }
}

function buildStart(labels: readonly string[]): Float32Array {
  const out = new Float32Array(labels.length);
  for (let i = 0; i < labels.length; i++) {
    const p = splitTag(labels[i]).prefix;
    out[i] = p === "I" || p === "E" ? NEG_INF : 0;
  }
  return out;
}

function buildTransitions(labels: readonly string[]): Float32Array[] {
  const tags = labels.map(splitTag);
  const n = labels.length;
  const out: Float32Array[] = [];
  for (let i = 0; i < n; i++) {
    const row = new Float32Array(n);
    for (let j = 0; j < n; j++) {
      row[j] = allowed(tags[i], tags[j]) ? 0 : NEG_INF;
    }
    out.push(row);
  }
  return out;
}

function logSoftmax(scores: Float32Array | number[], offset: number, len: number): Float32Array {
  let max = scores[offset];
  for (let i = 1; i < len; i++) if (scores[offset + i] > max) max = scores[offset + i];
  let sum = 0;
  for (let i = 0; i < len; i++) sum += Math.exp(scores[offset + i] - max);
  const logSum = Math.log(sum) + max;
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) out[i] = scores[offset + i] - logSum;
  return out;
}

/**
 * @param logits Flat row-major `[T * L]` logits.
 * @param T number of timesteps.
 * @param labels label name per id; length L.
 * @returns per-timestep label index, length T.
 */
export function viterbiDecode(
  logits: Float32Array,
  T: number,
  labels: readonly string[],
): Int32Array {
  if (T === 0) return new Int32Array(0);
  const L = labels.length;
  if (logits.length !== T * L) {
    throw new Error(`logits length ${logits.length} != T*L (${T}*${L})`);
  }

  const trans = buildTransitions(labels);
  const start = buildStart(labels);

  const delta = new Float32Array(L);
  const next = new Float32Array(L);
  const bp: Int32Array[] = new Array(T);
  for (let t = 0; t < T; t++) bp[t] = new Int32Array(L);

  const lp0 = logSoftmax(logits, 0, L);
  for (let i = 0; i < L; i++) delta[i] = start[i] + lp0[i];

  for (let step = 1; step < T; step++) {
    const lp = logSoftmax(logits, step * L, L);
    for (let j = 0; j < L; j++) {
      let best = NEG_INF;
      let bestPrev = 0;
      for (let i = 0; i < L; i++) {
        const s = delta[i] + trans[i][j];
        if (s > best) {
          best = s;
          bestPrev = i;
        }
      }
      next[j] = best + lp[j];
      bp[step][j] = bestPrev;
    }
    delta.set(next);
  }

  let best = 0;
  for (let i = 1; i < L; i++) if (delta[i] > delta[best]) best = i;

  const out = new Int32Array(T);
  out[T - 1] = best;
  for (let step = T - 1; step >= 1; step--) {
    out[step - 1] = bp[step][out[step]];
  }
  return out;
}
